import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect } from 'react';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import * as yup from 'yup';

import NoDataAlert from '~components/Common/NoDataAlert';
import { FilterMenuItemLoadingReason } from '~components/Filters/FilterMenuItem';
import CounterFormField from '~components/FormFields/CounterFormField';
import { SelectedAssignment } from '~components/Job/AssignJobToView/hooks/useAssignDriverMenu';
import {
  LoadingSpinner,
  OverflowAwareText,
} from '~components/Order/ordersDispatchStyledComponents';
import VendorTrucksAssignedSummary from '~components/TruckCapacity/VendorTrucksAssignedSummary';
import { JobAssignmentType } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { AccountCapacityTypeahead } from '~hooks/useAccount/models';
import { DriverTypeahead } from '~hooks/useDrivers/models';
import { useStores } from '~store';
import { getDriverInfoForCards } from '~utils/drivers/driver-utils';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import InfiniteScrollLoadingIndicator from '../InfiniteScrollLoadingIndicator';

const DRIVERS_ROW_HEIGHT_IN_PX = 68;
const VENDORS_ROW_HEIGHT_IN_PX = 46;

const assigneeSchema = yup.object().shape({
  id: yup.string().trim().uuid().required(),
  count: yup.number().required(),
});

const assigneesSchema = yup.object().shape({
  assignees: yup.array(assigneeSchema.required()).required(),
});

type AssigneesDTO = yup.InferType<typeof assigneesSchema>;

interface AssigneesListProps {
  assigneeType: JobAssignmentType;
  assignees: SelectedAssignment[];
  fetchMore?: () => void;
  hasMore?: boolean;
  loadingReason?: FilterMenuItemLoadingReason;
  showNoResultsMessage?: boolean;
  sx?: SxProps;
}

const AssigneesList = observer(
  ({
    assigneeType,
    assignees,
    fetchMore,
    hasMore,
    loadingReason,
    showNoResultsMessage,
    sx,
  }: AssigneesListProps) => {
    const { bulkAssignMultipleStore } = useStores();

    const hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled = useFeatureFlag({
      featureFlagKey: FeatureFlags.hideDriversVendorNameIfOwnerOperator,
    });

    const form = useForm<AssigneesDTO>({
      resolver: yupResolver(assigneesSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: { assignees: [] },
    });
    const formAssigneesArray = useFieldArray({
      control: form.control,
      name: 'assignees',
    });

    useEffect(() => {
      const formAssignees: AssigneesDTO['assignees'] = assignees.map((assignee) => {
        const selection = bulkAssignMultipleStore.getSelectedAssignee(assignee.id);
        const count = selection?.count ?? 0;

        return { id: assignee.id, count };
      });

      formAssigneesArray.replace(formAssignees);
    }, [assignees, formAssigneesArray.replace]);

    if (showNoResultsMessage) {
      const isTargetTypeVendor = assigneeType === JobAssignmentType.VENDOR;
      const entity = isTargetTypeVendor
        ? t('form_fields.vendor').toLocaleLowerCase()
        : t('form_fields.driver').toLocaleLowerCase();

      return (
        <NoDataAlert
          message={t('dispatch.order.no_assignees_for_bulk_assign', { entity })}
          sx={{
            height: `${isTargetTypeVendor ? VENDORS_ROW_HEIGHT_IN_PX : DRIVERS_ROW_HEIGHT_IN_PX}px`,
          }}
        />
      );
    }

    return (
      <Box sx={sx}>
        <LoadingSpinner
          isVisible={loadingReason === FilterMenuItemLoadingReason.SEARCH_VALUE}
        />

        {loadingReason === FilterMenuItemLoadingReason.SEARCH_VALUE ? null : (
          <FormProvider {...form}>
            {formAssigneesArray.fields.map((_, index) => {
              const actualAssignee = assignees[index];

              if (!actualAssignee) {
                return null;
              }

              return (
                <React.Fragment key={actualAssignee.id}>
                  <AssigneeRow
                    index={index}
                    assignee={actualAssignee}
                    hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled={
                      hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled
                    }
                  />

                  {index === assignees.length - 1 && hasMore && (
                    <InfiniteScrollLoadingIndicator
                      alignItems="center"
                      display="flex"
                      height="50px"
                      isLoading={
                        loadingReason === FilterMenuItemLoadingReason.INFINITE_SCROLL
                      }
                      justifyContent="center"
                      loadingIndicatorSize={30}
                      onFetchMore={fetchMore}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </FormProvider>
        )}
      </Box>
    );
  },
);

interface AssigneeRowProps {
  assignee: SelectedAssignment;
  hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled?: boolean;
  index: number;
}

const AssigneeRow = observer(
  ({
    assignee,
    hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled = false,
    index,
  }: AssigneeRowProps) => {
    const theme = useTheme();
    const { bulkAssignMultipleStore } = useStores();

    const form = useFormContext();
    const rowCountFieldName = `assignees.${index}.count`;
    const watchedRowCount = form.watch(rowCountFieldName) as number;

    const selection = bulkAssignMultipleStore.getSelectedAssignee(assignee.id);
    const selectionCount = selection?.count ?? 0;
    const isSelected = selectionCount > 0;

    const handleCheckboxChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        form.setValue(rowCountFieldName, event.target.checked ? 1 : 0);
      },
      [form.setValue, rowCountFieldName],
    );

    useEffect(() => {
      if (watchedRowCount > 0) {
        bulkAssignMultipleStore.updateSelectedAssignee({
          id: assignee.id,
          type: assignee.type,
          count: watchedRowCount,
        });
      } else {
        bulkAssignMultipleStore.unselectAssignee(assignee.id);
      }
    }, [watchedRowCount, assignee.id, assignee.type]);

    useEffect(() => {
      form.setValue(rowCountFieldName, selectionCount);
    }, [form.setValue, rowCountFieldName, selectionCount]);

    const content =
      assignee.entity instanceof AccountCapacityTypeahead ? (
        <VendorTrucksAssignedSummary vendor={assignee.entity} />
      ) : (
        <DriverAssigneeContent
          driver={assignee.entity}
          hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled={
            hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled
          }
        />
      );

    return (
      <Box
        sx={{
          borderRadius: theme.brandV2.borderRadius,
          minHeight: `${assignee.type === JobAssignmentType.VENDOR ? VENDORS_ROW_HEIGHT_IN_PX : DRIVERS_ROW_HEIGHT_IN_PX}px`,
          p: 1,
          '&:hover': { bgcolor: theme.brandV2.colors.treadGray8, cursor: 'pointer' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ minWidth: '20px', flex: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: selection?.priority
                  ? theme.brandV2.colors.treadGray3
                  : theme.brandV2.colors.treadGray6,
                fontWeight: 600,
              }}
            >
              {selection?.priority ? `${selection.priority}.` : '-'}
            </Typography>
          </Box>

          <Box sx={{ minWidth: '20px', flex: 0 }}>
            <Checkbox
              size="small"
              sx={{
                '&.MuiCheckbox-root': {
                  color: theme.brandV2.colors.treadGray6,
                  mt: '-10px',
                  p: 0,
                },
                '&.Mui-checked': { color: theme.brandV2.colors.treadOrange },
              }}
              checked={isSelected}
              onChange={handleCheckboxChange}
              data-ui="page-dispatch__assignees__checkbox"
            />
          </Box>

          <Box sx={{ flex: 1 }}>{content}</Box>

          <Box sx={{ minWidth: '120px', flex: 0, alignSelf: 'center' }}>
            <CounterFormField
              control={form.control}
              max={999}
              min={0}
              name={`assignees.${index}.count`}
            />
          </Box>
        </Box>
      </Box>
    );
  },
);

interface DriverAssigneeContentProps {
  driver: DriverTypeahead;
  hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled: boolean;
}

function DriverAssigneeContent({
  driver,
  hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled,
}: DriverAssigneeContentProps) {
  const theme = useTheme();
  const info = getDriverInfoForCards(
    driver,
    driver.companyShare?.senderCompany,
    hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled,
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <OverflowAwareText
          variant="body2"
          sx={{
            color: info.isAvailable
              ? theme.brandV2.colors.treadBlue2
              : theme.brandV2.colors.treadBlack,
            cursor: 'auto',
            fontWeight: 600,
            width: 'auto',
          }}
        >
          {info.equipmentName && (
            <>
              <Typography
                component="span"
                sx={{
                  color: theme.brandV2.colors.treadBlack,
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                }}
              >
                {info.equipmentName}
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: theme.brandV2.colors.treadBlack,
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                }}
              >
                {' • '}
              </Typography>
            </>
          )}

          <Typography
            component="span"
            sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
          >
            {info.fullName}
          </Typography>
        </OverflowAwareText>
      </Box>

      {info.truckType && (
        <OverflowAwareText
          sx={{ color: theme.brandV2.colors.treadGray2, fontSize: '11px' }}
        >
          {info.truckType}
        </OverflowAwareText>
      )}

      {info.vendorName && (
        <OverflowAwareText
          sx={{ color: theme.brandV2.colors.treadGray2, fontSize: '11px' }}
        >
          {info.vendorName}
        </OverflowAwareText>
      )}
    </Box>
  );
}

export default AssigneesList;
